/*
 * Typings for most of the data that's sent from the server. If you have a C# data class that's sent over the wire it's probably best to have a corresponding typing here.
 */

export interface Vector3 {
    x: number;
    y: number;
    z: number;
}

export interface Quaternion {
    x: number;
    y: number;
    z: number;
    w: number;
}

export interface ViewModel {
    guid: string;
    devSettings: DevSettings;
    viewToken: string;
}

export interface ModelBase {
    viewParameters?: ViewParameters;
    comments?: Comment[];
    role?: string;
    tools?: Tools;
    devSettings: DevSettings;
    uiConstants: UiConstants;
    recaptchaKey: string;
    measurementCalibration: Calibration;
    externalDataFile?: string;
    externalData?: any;
    tourDataFile?: string;
    tourData?: TourData;
    userDefinedAttributes: { [key: string]: string };
}

export interface Model extends ModelBase {
    guid: string;
    modelId?: string;
    modelName?: string;
    projectName?: string;
    displayName?: boolean;
    displayTitle?: boolean;
    type?: string;
    latestVersionQueryId?: string;
    otherModels?: string[];
   
    idMap?: any;
    buildOrders?: BuildOrder[];
    webDataFile?: string;
    webData?: { [key: string]: WebData };

    externalData?: ExternalData;
}

export interface ComboViewModel {
    comboModelId: string;
    primarySubModelId: string;
    devSettings: DevSettings;
}

export interface ComboModel extends ModelBase {
    comboModelId: string;
    models: SubModel[];
}

export interface SubModel {
    guid: string;
    type: string;
    position: Vector3;
    rotation: Quaternion;
    scale: Vector3;
    name: string;
    defaultVisibility: boolean
}

export interface ViewerState {
    currentRole?: ModelRole;
    comments?: { [key: string]: Comment };
    measurements?: any[];
    filters?: { [key: string]: QuickFilter };
    markups?: { [key: string]: Markup };
}

export interface Markup {
    id: string;
    name: string;
    svg: string;
    posVector?: Vector3;
    pivotVector?: Vector3;
    rotationQuaternion?: Quaternion;
}

export interface ViewParameters {
    posVector?: Vector3;
    pivotVector?: Vector3;
    targetVector?: Vector3;
    rotationQuaternion?: Quaternion;
    selectedNodes?: string[];
    isolatedNodes?: string[];
    hiddenNodes?: string[];
    lightingEnv?: string;
    lightingEnvBg?: boolean;
    notice?: ModelNotice;
}

export interface Comment {
    id: string;
    text: string;
    position: THREE.Vector3;
}

export interface QuickFilter {
    id: string;
    name: string;
    text: string;
}

export interface Tools {
    extensions?: ForgeExtension[];
    hiddenTools: string[];
    source: string;
    hasIdMap: boolean;
    modelSettingsId?: string;
}

export interface DevSettings {
    environment: string;
}

export interface UiConstants {
    buildOrderInterval: number;
    explodeDuration: number;
    explodeStart: number;
}

export enum NoticeLevel {
    Error = 1,
    Warning = 2
}

export interface ModelNotice {
    level: NoticeLevel;
    message: string;
}

export interface ForgeExtension {
    name?: string;
    url?: string;
}

export interface Model360 {
    configId?: string;
    group?: string;
    image?: string;
    pitch?: number;
    yaw?: number;
    hfov?: number;
    autorotate?: number;
}

export interface Color {
    r: number;
    g: number;
    b: number;
    a: number;
}

export interface ColorData {
    id: string;
    description: string;
    color: Color;
    vector: THREE.Vector4; // Client-side only for color caching
}

export interface ColorScheme {
    id: string;
    description: string;
    defaultColor: Color;
    hideByDefault: boolean;
    colors: { [key: string]: ColorData; };
}

export interface ColorSchemes {
    customColorSchemes: ColorScheme[];
}

export interface RangePoint {
    value: number;
    color: Color;
}

export interface RangeColorScheme {
    id: string;
    description: string;
    defaultColor: Color;
    hideByDefault: boolean;
    slider: boolean;
    range: RangePoint[];
}

export interface RangeColorSchemes {
    description: string;
    selectionLabel: string;
    colorRanges: RangeColorScheme[];
}

export interface ExternalData {
    idSchemes?: ColorSchemes;
    rangeSchemes?: RangeColorSchemes;
    idProperty?: IdProperty;
    propertyDefinitions: { [key: string]: PropertyDefinition; };
    propertyValues: { [key: string]: { [key: string]: string } };
    nodes?: NodeData;

    paramsToDbIds?: { [key: string]: any};
}

export interface TourData {
    pois: PoiData[];
    defaultPoiName?: string;
    paramsToDbIds?: { [key: string]: any };
}

export interface PoiData {
    name: string;
    visibleElementIds: string[];
    visibleElementIdProperties: IdProperty[];
    description: string;
}

export interface NodeData {
    propertyDefinitions: { [key: string]: PropertyDefinition; };
    propertyValues: { [key: string]: { [key: string]: string } };
}

export interface IdProperty {
    displayCategory: string;
    displayName: string;
}

export interface PropertyDefinition {
    name: string;
    category?: string;
    type: string;
    description: string;
    format?: string;
    legendId?: string;

    // Front-end only information to tell if this is a node property, not element. Used only for coloring purposes.
    isNode?: boolean;
}

export interface Measurement {
    point1: Vector3;
    point2: Vector3;
    linePoint1?: Vector3;
    linePoint2?: Vector3;
    value: string;
}

export interface Calibration {
    unit?: string;
    precision?: number;
    scale?: number;
}

export interface Setting {
    label: string;
    type: string;
    viewerSetting?: boolean;
    values?: any[];
    default: any;
}

export interface ModelRole {
    name: string;
    displayName: string;
    universalSearch?: boolean;
    uiSettings?: ModelRoleUiSettings;
    selectionRules?: ModelSelectionRules;
}

export interface ModelRoleUiSettings {
    explodeLowerLimit: number;
    explodeUpperLimit: number;
    hiddenLayers: string[];
}

export interface ModelSelectionRules {
    selectors: ParentSelector[];
}

export interface ParentSelector {
    displayCategory: string;
    displayName: string;
    displayValue: string;
}

export interface BuildOrder {
    name: string;
    buildPlanFile: string;
    assemblyFile: string;
}

export interface WebData {
    title: string;
    url: string;
}

export interface ClientConfig {
    uploadChunkSize: number;
}

export interface IModelNode {
    modelId: number;
    nodeId: number;
}

export class ModelNode implements IModelNode {
    modelId: number;
    nodeId: number;

    constructor(modelNumber: number, nodeNumber: number) {
        this.modelId = modelNumber;
        this.nodeId = nodeNumber;
    }
}

export type SettingsMap = { [key: string]: Setting; };

export class SdEvents {
    static SD_ROLE_CHANGE = "SD_ROLE_CHANGE";
    static ID_MAP_LOADED = "ID_MAP_LOADED";
    static PARAMS_TO_DBIDS_MAPPED = "PARAMS_TO_DBIDS_MAPPED";
    static EXTERNAL_DATA_LOADED = "EXTERNAL_DATA_LOADED";
    static EXTERNAL_DATA_CHANGED = "EXTERNAL_DATA_CHANGED";
    static SD_OBJECT_TREE_CREATED = "SD_OBJECT_TREE_CREATED";
}

export class UserDefinedAttributes {
    static FORGE_DISABLE_REFLECTION = "forgeDisableReflection";
    static FORGE_EXPOSURE = "forgeExposure";
    static FORGE_UP_VECTOR = "forgeUpVector";
    static OPEN_FILTERED_PROPS_ON_SELECT = "openFilteredPropsOnSelect";
    static ALLOWED_COUNTRIES = "allowedCountries";
    static SHADOW_RADIUS = "shadowRadius";
    static SHADOW_INTENSITY = "shadowIntensity";
}

/**
 * Used URL parameters
 */
export class SdUrlParameters {
    static SEARCH = "search";
    static EXACT_MATCH = "exact";
    static SEARCH_ISOLATE = "isolate";
    static SEARCH_ZOOMTOFIT = "zoomtofit";
    static SELECTION = "selectionParams";
    static ISOLATION = "isolationParams";
    static COLOR_ONLY_ISOLATED = "coi";
    static RUUKKI_PRODUCTION_ID = "ext.productionId";
}

export interface BcfInfo {
    Markups: BcfMarkup[];
    Version: Version;
}

export interface BcfMarkup {
    Header: Header[];
    Topic: Topic;
    Comment: any[];
    Viewpoints: Viewpoint[];
    noNamespaceSchemaLocation: string;
}

export interface Header {
    Filename: string;
    Date: Date;
    IfcProject: string;
    isExternal: boolean;
}

export interface Topic {
    Guid: string;
    TopicType: string;
    TopicStatus: string;
    Title: string;
    Index: number;
    CreationDate: Date;
    CreationAuthor: string;
    ModifiedDate: Date;
    ModifiedAuthor: string;
    DueDate: Date;
    AssignedTo: string;
    Description: string;
}

export interface Viewpoint {
    Guid: string;
    Viewpoint: string;
    Snapshot: string;
    Index: number;
    VisualizationInfo: VisualizationInfo;
    SnapshotContents: string;
}

export interface VisualizationInfo {
    Guid: string;
    Components: Components;
    PerspectiveCamera: PerspectiveCamera;
    OrthogonalCamera: OrthogonalCamera;
    Lines: Line[];
    Bitmap: any[];
    noNamespaceSchemaLocation: string;
    ClippingPlanes?: ClippingPlane[];
}

export interface ClippingPlane {
    Location: CameraDirection;
    Direction: CameraDirection;
}

export interface CameraDirection {
    X: number;
    Y: number;
    Z: number;
}

export interface Components {
    Selection: Component[];
    Coloring: Coloring[];
    ViewSetupHints: ViewSetupHints;
    Visibility: Visibility;
    ComponentList: Component[];
}

export interface Coloring {
    Component: Component[];
    Color: string;
}

export interface Component {
    IfcGuid: string;
    OriginatingSystem: OriginatingSystem;
    AuthoringToolId: string;
    Visible: boolean;
    Selected: boolean;
}

export enum OriginatingSystem {
    TeklaStructures = "Tekla Structures",
}

export interface ViewSetupHints {
    SpacesVisible: boolean;
    SpaceBoundariesVisible: boolean;
    OpeningsVisible: boolean;
}

export interface Visibility {
    Exceptions: Component[];
    DefaultVisibility: boolean;
}

export interface Line {
    StartPoint: CameraDirection;
    EndPoint: CameraDirection;
}

export interface PerspectiveCamera {
    CameraViewPoint: CameraDirection;
    CameraDirection: CameraDirection;
    CameraUpVector: CameraDirection;
    FieldOfView: number;
}

export interface OrthogonalCamera {
    CameraViewPoint: CameraDirection;
    CameraDirection: CameraDirection;
    CameraUpVector: CameraDirection;
    ViewToWorldScale: number;
}

export interface Version {
    DetailedVersion: string;
    VersionId: string;
    noNamespaceSchemaLocation: string;
}